import { BaseModel } from '@/core/models/base.model'
import { SearchEntityType, SearchResult } from '../types/search.types'
import { interestGroupBadgesOf, type Badge } from '@/shared/types/badge/badge'
import {
  containsSearchHighlight,
  stripSearchHighlightMarkup,
} from '@/shared/utilities/search-highlight-markup'
import { withoutSoundCommand } from '@/shared/mentions/sound-command'
import { routes } from '@/shared/routes'

interface RawSearchResult {
  data?: Record<string, unknown> | null
  entity_type?: string
  identifier?: string | number | null
  highlight?: unknown
}

const KNOWN_ENTITIES: SearchEntityType[] = [
  'business',
  'hive',
  'event',
  'post',
  'product',
  'service_funnel',
  'user',
]

/**
 * Type guard used by the API layer to drop rows the client cannot label or
 * route (an unknown `entity_type` used to be silently coerced to `business`,
 * mislabeling and misrouting the row).
 */
export function isKnownSearchEntityType(value: unknown): value is SearchEntityType {
  return typeof value === 'string' && KNOWN_ENTITIES.includes(value as SearchEntityType)
}

/** Keep only string-valued highlight snippets; `null` when nothing usable. */
function parseHighlight(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  )
  if (entries.length === 0) return null
  return Object.fromEntries(entries)
}

export class SearchResultModel extends BaseModel<SearchResult> implements SearchResult {
  data: Record<string, unknown> | null
  entity_type: SearchEntityType
  identifier: string | number | null
  highlight: Record<string, string> | null

  constructor(data: SearchResult) {
    super()
    this.data = data.data
    this.entity_type = data.entity_type
    this.identifier = data.identifier
    this.highlight = data.highlight ?? null
  }

  serialize(): SearchResult {
    return {
      data: this.data,
      entity_type: this.entity_type,
      identifier: this.identifier,
      highlight: this.highlight,
    }
  }

  static fromJSON(json: RawSearchResult | SearchResult): SearchResultModel {
    const raw = json as RawSearchResult
    if (!isKnownSearchEntityType(raw.entity_type)) {
      // Callers must pre-filter with `isKnownSearchEntityType` (see
      // `search.api.ts`); coercing to 'business' here used to misroute rows.
      throw new Error(`Unknown search entity_type: ${String(raw.entity_type)}`)
    }
    return new SearchResultModel({
      data: raw.data ?? null,
      entity_type: raw.entity_type,
      identifier: raw.identifier ?? null,
      highlight: parseHighlight(raw.highlight),
    })
  }
}

interface DisplayFields {
  title: string
  subtitle?: string
  thumbnail?: string
  href: string
}

/** Project a raw search-result payload into the fields needed for rendering. */
export function pickResultDisplay(result: SearchResult): DisplayFields {
  const id = String(result.identifier ?? '')
  const data = result.data ?? {}
  const highlight = result.highlight ?? {}
  /** Fields whose content actually ended up in title/subtitle. */
  const shown = new Set<string>()
  const str = (k: string) => (typeof data[k] === 'string' ? (data[k] as string) : undefined)
  const snippet = (k: string) =>
    containsSearchHighlight(highlight[k]) ? highlight[k] : undefined
  /**
   * Display text for a field: prefer the server's match snippet (so the row
   * shows WHERE it matched), falling back to the raw payload value.
   */
  const text = (k: string) => {
    const value = snippet(k) ?? str(k)
    if (value !== undefined) shown.add(k)
    return value
  }

  return withMatchReason(baseDisplay())

  function baseDisplay(): DisplayFields {
    switch (result.entity_type) {
      case 'business':
        return {
          title: text('name') ?? 'Business',
          subtitle: text('summary') ?? text('handle'),
          thumbnail: str('logo'),
          href: id ? routes.business(id) : routes.businesses(),
        }
      case 'hive':
        return {
          title: text('name') ?? 'Hive',
          subtitle: text('description') ?? text('handle'),
          thumbnail: str('cover_image') ?? str('avatar_url'),
          href: id ? routes.hive(id) : routes.hives,
        }
      case 'event':
        return {
          title: text('name') ?? 'Event',
          subtitle: str('start_date') ?? text('description'),
          thumbnail: str('image'),
          href: id ? routes.event(id) : routes.events,
        }
      case 'post': {
        const postText = withoutSoundCommand(text('text') ?? text('content') ?? '')
        return {
          title: postText ? postText : 'Post',
          // Always show the author so a post matched via `posted_by_name`
          // (highlighted snippet preferred) explains itself on sight.
          subtitle: text('posted_by_name'),
          thumbnail: undefined,
          href: id ? routes.post(id) : routes.home,
        }
      }
      case 'product':
        return {
          title: text('name') ?? 'Product',
          subtitle: text('summary') ?? text('description'),
          thumbnail: str('image'),
          href: id ? routes.product(id) : routes.shop,
        }
      case 'service_funnel': {
        const serviceTitle = text('name') ?? 'Service'
        const serviceQuery = stripSearchHighlightMarkup(serviceTitle)
        return {
          title: serviceTitle,
          subtitle: text('profession_title') ?? text('description'),
          thumbnail: str('image'),
          href: routes.profinder.index({ q: serviceQuery, fid: id, autoSearch: true }),
        }
      }
      case 'user':
        return {
          title: [text('first_name'), text('last_name')].filter(Boolean).join(' ') || 'Person',
          subtitle: text('bio'),
          thumbnail: str('avatar'),
          href: id ? routes.profile(id) : routes.people,
        }
    }
  }

  /**
   * When neither the title nor the subtitle carries a highlight, the visible
   * row contains nothing from the query (e.g. a business matched via
   * `service_funnel_names`). Surface the first highlighted field that is not
   * already displayed as a "Matched: …" subtitle so the hit explains itself.
   */
  function withMatchReason(display: DisplayFields): DisplayFields {
    if (containsSearchHighlight(display.title) || containsSearchHighlight(display.subtitle)) {
      return display
    }
    const matched = Object.entries(highlight).find(
      ([field, value]) => !shown.has(field) && containsSearchHighlight(value)
    )
    if (!matched) return display
    return { ...display, subtitle: `Matched: ${stripSearchHighlightMarkup(matched[1])}` }
  }
}

/**
 * The interest-group badges the server sent on a search row, for rendering.
 * A search document's `badges` is untyped JSON, so each entry is shape-checked
 * before the shared `kind` pick (ADR-0085) runs over it.
 */
export function pickResultInterestGroupBadges(result: SearchResult): Badge[] {
  const badges = result.data?.badges
  if (!Array.isArray(badges)) return []
  return interestGroupBadgesOf(badges.filter(isBadgeShaped))
}

function isBadgeShaped(value: unknown): value is Badge {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<Badge>
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.icon === 'string' &&
    typeof candidate.type === 'string'
  )
}
